import { useCallback, useEffect, useRef, useState } from "react";
import { API_URL, client } from "../lib/rebaseClient";

/**
 * What every mail screen shares: the shapes it reads, the calls it makes, and
 * the handful of rules about how a conversation is described.
 *
 * Rows are `type`s, not `interface`s, and that is load-bearing: only a type
 * alias gets an implicit index signature, so only a type alias satisfies the
 * SDK's `M extends Record<string, unknown>` without a cast at every call site —
 * and a cast there would switch off the one check that catches a renamed column.
 *
 * Relations read back under their camelCase name (`prospectId`, `mailboxId`),
 * with no snake_case key at all. Measured against the running server; reading
 * one by its column name here would be an ordinary, silent `undefined` — which
 * `backend/test/filter-field-names.test.ts` greps this tree for.
 */

export type Thread = {
    id: string;
    subject?: string | null;
    counterpart_name?: string | null;
    participants?: string[] | null;
    last_snippet?: string | null;
    last_message_at?: string | null;
    last_direction?: "inbound" | "outbound" | null;
    last_inbound_at?: string | null;
    last_outbound_at?: string | null;
    message_count?: number | null;
    kind?: "person" | "automated" | "broadcast" | null;
    /** What the screen decided it is — see backend/src/agent/inbox/triage.ts. */
    category?: "conversation" | "pitch" | "transactional" | "notification" | "newsletter" | "spam" | null;
    /** One line on why it was filed where it is. */
    filed_reason?: string | null;
    triaged_by?: "model" | "history" | "rule" | "headers" | "person" | null;
    triaged_at?: string | null;
    status?: "open" | "closed" | null;
    unread?: boolean | null;
    needs_reply?: boolean | null;
    follow_up_at?: string | null;
    gmail_thread_id?: string | null;
    linked_by?: "auto" | "manual" | null;
    prospectId?: string | null;
    companyId?: string | null;
    mailboxId?: string | null;
};

export type Message = {
    id: string;
    threadId?: string | null;
    mailboxId?: string | null;
    direction?: "inbound" | "outbound" | null;
    status?: "received" | "sent" | "failed" | null;
    from_email?: string | null;
    from_name?: string | null;
    to_emails?: string[] | null;
    cc_emails?: string[] | null;
    subject?: string | null;
    body_text?: string | null;
    body_html?: string | null;
    body_snippet?: string | null;
    sent_at?: string | null;
    actor?: string | null;
    error?: string | null;
    has_attachments?: boolean | null;
    attachment_names?: string[] | null;
    imap_uid?: number | null;
};

export type Person = {
    id: string;
    handle?: string | null;
    display_name?: string | null;
    email?: string | null;
    role?: string | null;
    company?: string | null;
    status?: string | null;
};

export type Company = {
    id: string;
    name?: string | null;
    domain?: string | null;
};

export interface MailboxSummary {
    id: string;
    name: string;
    address: string;
    aliases: string[];
    fromName: string | null;
    color: string;
    provider: "gmail" | "fastmail" | "icloud" | "custom";
    status: string;
    lastSyncedAt: string | null;
    lastError: string | null;
    needsAttention: string | null;
    passwordSet: boolean;
    canRead: boolean;
    canSend: boolean;
    readProblems: string[];
    sendProblems: string[];
    signature: string | null;
    outreachDefault: boolean;
    syncEnabled: boolean;
    sendEnabled: boolean;
    passwordHelp: string | null;
}

export interface Draft {
    subject: string;
    body: string;
    facts: string[];
    warnings: string[];
}

// ── Calls ────────────────────────────────────────────────────────────────

const invoke = <T,>(path: string, method: "GET" | "POST", body?: unknown) =>
    client.functions.invoke<T>("inbox", body, { method, path });

export const inboxApi = {
    mailboxes: () => invoke<{ mailboxes: MailboxSummary[]; keyConfigured: boolean }>("/mailboxes", "GET"),
    createMailbox: (fields: Record<string, unknown>) => invoke<{ mailbox: MailboxSummary }>("/mailboxes", "POST", fields),
    setPassword: (id: string, password: string) =>
        invoke<{ mailbox: MailboxSummary }>(`/mailboxes/${encodeURIComponent(id)}/password`, "POST", { password }),
    testMailbox: (id: string) =>
        invoke<{ imap: { ok: boolean; error?: string; folder?: string; sentFolder?: string; messages?: number }; smtp: { ok: boolean; error?: string } }>(
            `/mailboxes/${encodeURIComponent(id)}/test`, "POST", {}),
    resetMailbox: (id: string, purge = false) => invoke(`/mailboxes/${encodeURIComponent(id)}/reset`, "POST", { purge }),
    sync: (options: { force?: boolean; mailbox?: string | null } = {}) => {
        const query = new URLSearchParams();
        if (options.force) query.set("force", "1");
        if (options.mailbox) query.set("mailbox", options.mailbox);
        const text = query.toString();
        return invoke<{ success?: boolean; skipped?: boolean; kept?: number; notes?: string }>(`/sync${text ? `?${text}` : ""}`, "POST", {});
    },
    reply: (threadId: string, body: { text: string; to?: string[]; cc?: string[]; subject?: string; mailboxId?: string | null }) =>
        invoke<{ threadId: string; rejected?: string[] }>(`/threads/${encodeURIComponent(threadId)}/reply`, "POST", body),
    send: (body: { mailboxId?: string | null; to: string[]; cc?: string[]; subject: string; text: string; prospectId?: string | null; companyId?: string | null }) =>
        invoke<{ threadId: string; rejected?: string[] }>("/send", "POST", body),
    draft: (body: { threadId?: string | null; prospectId?: string | null; companyId?: string | null; to?: string | null; instruction?: string; current?: { subject?: string; body?: string } }) =>
        invoke<Draft>("/draft", "POST", body),
    link: (threadId: string, link: { prospect?: string | null; company?: string | null }) =>
        invoke(`/threads/${encodeURIComponent(threadId)}/link`, "POST", link),
    kind: (threadId: string, body: { kind: "person" | "automated"; scope?: "address" | "domain" }) =>
        invoke<{ rule?: string }>(`/threads/${encodeURIComponent(threadId)}/kind`, "POST", body)
};

/**
 * Download an attachment. Not through `invoke`: the body is a file, and the
 * client only knows how to hand back JSON.
 */
export async function downloadAttachment(messageId: string, name: string): Promise<void> {
    const token = await client.resolveToken().catch(() => null);
    const url = `${API_URL}/api/functions/inbox/attachment?message=${encodeURIComponent(messageId)}&name=${encodeURIComponent(name)}`;
    const response = await fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!response.ok) {
        const body = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(body.error ?? `The attachment could not be fetched (${response.status}).`);
    }
    const blob = await response.blob();
    const href = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = href;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(href), 30_000);
}

/** A function's refusal in its own words — the endpoints write them for a person. */
export function describeError(err: unknown): string {
    const api = err as { body?: { error?: string; problems?: string[]; hint?: string }; message?: string };
    const problems = Array.isArray(api.body?.problems) && api.body!.problems!.length ? ` — ${api.body!.problems!.join("; ")}` : "";
    const hint = api.body?.hint ? ` ${api.body.hint}` : "";
    return `${api.body?.error ?? api.message ?? String(err)}${problems}${hint}`;
}

// ── The mailbox list, shared by every screen that sends ──────────────────

export function useMailboxes(): { mailboxes: MailboxSummary[]; loaded: boolean; error?: string; reload: () => Promise<void> } {
    const [mailboxes, setMailboxes] = useState<MailboxSummary[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const reload = useCallback(async () => {
        try {
            const result = await inboxApi.mailboxes();
            setMailboxes(result.mailboxes ?? []);
            setError(undefined);
        } catch (err) {
            setError(describeError(err));
        } finally {
            setLoaded(true);
        }
    }, []);
    useEffect(() => { void reload(); }, [reload]);
    return { mailboxes, loaded, error, reload };
}

/**
 * Read the mailboxes while somebody is looking.
 *
 * The cron is the backstop for the hours when nobody is; this is what makes a
 * reply appear about a minute after it arrives rather than up to fifteen. The
 * tab must be visible (and it polls on return, the moment the answer is most
 * wanted), one request is in flight at most, and the server's own floor bounds
 * the many-tab case that no browser can. The background poll never forces.
 */
export function useAutoSync(poll: () => Promise<void>, enabled: boolean, intervalMs = 60_000): void {
    const latest = useRef(poll);
    latest.current = poll;
    const inFlight = useRef(false);
    useEffect(() => {
        if (!enabled) return;
        const run = async () => {
            if (inFlight.current || document.visibilityState !== "visible") return;
            inFlight.current = true;
            try { await latest.current(); } finally { inFlight.current = false; }
        };
        const timer = window.setInterval(() => void run(), intervalMs);
        document.addEventListener("visibilitychange", run);
        return () => {
            window.clearInterval(timer);
            document.removeEventListener("visibilitychange", run);
        };
    }, [enabled, intervalMs]);
}

// ── Describing a conversation ────────────────────────────────────────────

export const isPersonThread = (thread: Thread) => !thread.kind || thread.kind === "person";

const FILED_LABELS: Record<string, string> = {
    pitch: "cold pitch",
    transactional: "invoice / receipt",
    notification: "notification",
    newsletter: "newsletter",
    spam: "spam"
};

/** What a thread that is not a conversation is, in two words. */
export function filedLabel(thread: Pick<Thread, "kind" | "category">): string {
    if (thread.kind === "broadcast") return "sent to nobody in particular";
    return (thread.category && FILED_LABELS[thread.category]) || "machine";
}

export function threadTitle(thread: Pick<Thread, "subject">): string {
    return thread.subject?.trim() || "(no subject)";
}

/** Who it is with, from the addresses: the first, and how many more. */
export function threadWith(thread: Pick<Thread, "participants">): string {
    const list = (thread.participants ?? []).filter(Boolean);
    if (list.length === 0) return "nobody";
    return list.length > 1 ? `${list[0]} +${list.length - 1}` : list[0];
}

/** Who it is with, as a person would say it: the linked name, their own, or the address. */
export function threadPerson(thread: Thread, linkedName?: string | null): string {
    return linkedName?.trim() || thread.counterpart_name?.trim() || threadWith(thread);
}

/** Midnight at the start of tomorrow, local — "today" ends there. */
export function startOfTomorrow(now = new Date()): Date {
    const next = new Date(now);
    next.setHours(24, 0, 0, 0);
    return next;
}

export function isWaitingOnUs(thread: Thread): boolean {
    return isPersonThread(thread) && thread.status !== "closed"
        && thread.last_direction === "inbound" && thread.needs_reply !== false;
}

/** Whole days since they last wrote, for a conversation waiting on us. */
export function daysWaiting(thread: Thread, now = new Date()): number | null {
    if (!isWaitingOnUs(thread) || !thread.last_inbound_at) return null;
    const then = new Date(thread.last_inbound_at);
    if (Number.isNaN(then.getTime())) return null;
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    return Math.max(0, Math.round((startOf(now) - startOf(then)) / 86_400_000));
}

export function isFollowUpDue(thread: Thread, now = new Date()): boolean {
    if (!thread.follow_up_at || thread.status === "closed") return false;
    return new Date(thread.follow_up_at).getTime() < startOfTomorrow(now).getTime();
}

/**
 * When a conversation comes back. `short` drops the "look again" for a place
 * that already says it — "Look again · look again today" reads twice.
 */
export function followUpLabel(thread: Thread, now = new Date(), short = false): string | null {
    if (!thread.follow_up_at) return null;
    const due = new Date(thread.follow_up_at);
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOf(due) - startOf(now)) / 86_400_000);
    const when = days === 0 ? "today"
        : days < 0 ? `${-days} day${days === -1 ? "" : "s"} overdue`
            : days === 1 ? "tomorrow" : `in ${days} days`;
    return short ? when : days < 0 ? `look again — ${when}` : `look again ${when}`;
}

/** 14:05 today, "yesterday", "3 days ago", then the date. */
export function sinceLabel(value: string | null | undefined, now = new Date()): string {
    if (!value) return "";
    const at = new Date(value);
    if (Number.isNaN(at.getTime())) return "";
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOf(now) - startOf(at)) / 86_400_000);
    if (days <= 0) return at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    return at.toLocaleDateString([], { day: "numeric", month: "short", ...(at.getFullYear() !== now.getFullYear() ? { year: "numeric" } : {}) });
}

/**
 * When, for the middle of a sentence: "today at 14:05", "yesterday", "3 days
 * ago", "on 5 Sep". `sinceLabel` is for a column, where a bare time is clear;
 * "They wrote 14:05" is not.
 */
export function whenInSentence(value: string | null | undefined, now = new Date()): string {
    if (!value) return "some time ago";
    const at = new Date(value);
    if (Number.isNaN(at.getTime())) return "some time ago";
    const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const days = Math.round((startOf(now) - startOf(at)) / 86_400_000);
    if (days <= 0) return `today at ${at.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
    if (days === 1) return "yesterday";
    if (days < 7) return `${days} days ago`;
    return `on ${at.toLocaleDateString([], { day: "numeric", month: "short" })}`;
}

export function fullStamp(value: string | null | undefined): string {
    if (!value) return "";
    const at = new Date(value);
    return Number.isNaN(at.getTime())
        ? ""
        : at.toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/**
 * Gmail's own view of a conversation. X-GM-THRID is decimal; Gmail's URLs use
 * it in hex. `authuser` picks the right account when several are signed in —
 * `/u/0/` would open whichever happened to be first.
 */
export function gmailThreadUrl(gmailThreadId: string | null | undefined, account?: string | null): string | null {
    const raw = String(gmailThreadId ?? "").trim();
    if (!/^\d+$/.test(raw)) return null;
    try {
        const hex = BigInt(raw).toString(16);
        return `https://mail.google.com/mail/${account ? `?authuser=${encodeURIComponent(account)}` : "u/0/"}#all/${hex}`;
    } catch {
        return null;
    }
}

/**
 * A sanitized message, as a document for a sandboxed frame.
 *
 * The HTML was already cleaned on the way in (`inbox/sanitize.ts`). This adds a
 * policy that forbids the frame fetching anything at all — no remote images, no
 * fonts, no scripts — and opens links in a new tab. The frame itself has an
 * empty `sandbox`, so nothing in it can run even if something got past both.
 */
export function readerDocument(html: string): string {
    return `<!doctype html><html><head><meta charset="utf-8">`
        + `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:">`
        + `<base target="_blank"><style>html,body{margin:0;padding:0}`
        + `body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;font-size:14px;line-height:1.6;color:#26292e;word-break:break-word;padding:12px}`
        + `a{color:#0070f4}img{max-width:100%}table{max-width:100%}`
        + `blockquote{margin:0 0 0 12px;padding-left:12px;border-left:2px solid #d1d4d9;color:#6e737b}`
        + `</style></head><body>${html}</body></html>`;
}

// ── Templates ────────────────────────────────────────────────────────────

export type Template = { id: string; name?: string | null; use?: string | null; subject?: string | null; body?: string | null };

/**
 * Fill a template's placeholders from who it is to. A placeholder with nothing
 * to fill it is left as it was, so the composer can say so — "Hi {{first_name}},"
 * sent to a stranger is the one mistake that tells them exactly how it was
 * written. "Hi {{first_name}}," with no name becomes "Hi,".
 */
export function fillTemplate(text: string, values: { name?: string | null; company?: string | null }): string {
    const name = (values.name ?? "").trim();
    const first = name.split(/\s+/)[0] ?? "";
    const company = (values.company ?? "").trim();
    let out = text;
    if (first) out = out.replace(/\{\{\s*first_name\s*\}\}/g, first);
    if (name) out = out.replace(/\{\{\s*name\s*\}\}/g, name);
    if (company) out = out.replace(/\{\{\s*company\s*\}\}/g, company);
    if (!first) out = out.replace(/^(\s*(?:Hi|Hello|Hola|Dear)),?\s*\{\{\s*first_name\s*\}\}\s*([,!])/im, "$1$2");
    return out;
}

export const hasUnfilledPlaceholder = (text: string) => /\{\{\s*[a-z_]+\s*\}\}/i.test(text);

/** Free-mail domains: a person at gmail.com is not a company called Gmail. */
export const FREEMAIL = new Set([
    "gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "live.com", "msn.com", "yahoo.com",
    "icloud.com", "me.com", "mac.com", "proton.me", "protonmail.com", "aol.com", "gmx.com", "gmx.net",
    "mail.com", "yandex.com", "zoho.com", "fastmail.com", "hey.com"
]);
